/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import React, { useEffect } from 'react';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';

import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { DynamedTab } from '../../../DynaMed/DynamedTab';
import { DYNAMED_TAB_LABEL, DYNAMED_TAB_VALUE, hasDynamedGuide } from '../../../DynaMed/dynamed';
import { selectPearlUserInfo } from '../../../Login/userSlice';
import { useDynamedTabUserFlag } from '../../../permissions';
import { fixupGuideName, selectDocumentVersionById, selectTabValue, tabPageSelected } from '../../chatSlice';
import styles from '../../Message.module.scss';
import { PageTabsType } from '../../../algorithm/types';
import { analyticsTrack } from '../../../analytics_utils';

type TabPageContainerProps = {
  children: React.ReactNode;
  page: PageTabsType;
  toolbar?: React.ReactNode;
};

/**
 * This component renders the tab header of a page, and the children must include the tab content.
 */
export function TabPageContainer({ children, page, toolbar }: TabPageContainerProps) {
  const documentVersion = useAppSelector((state) => selectDocumentVersionById(state, page.document_version_id));
  const dynamedTabEnabled = useDynamedTabUserFlag();
  const showDynamedTab = dynamedTabEnabled && hasDynamedGuide(documentVersion?.title);

  // We can't use defaultTabValue directly as the tabValue, because our store only stores one tabValue at a time,
  // but we could have multiple tab pages visible.
  // We could instead store it as part of the MessageType... hmm.
  const defaultTabValue = useAppSelector((state) => selectTabValue(state, page.document_version_id, page.id));
  const [tabValue, setTabValue] = React.useState(defaultTabValue || '0');
  const dispatch = useAppDispatch();
  const pearlUserInfo = useAppSelector(selectPearlUserInfo);

  useEffect(() => {
    if (!showDynamedTab && tabValue === DYNAMED_TAB_VALUE) {
      setTabValue(defaultTabValue || '0');
    }
  }, [defaultTabValue, showDynamedTab, tabValue]);

  useEffect(() => {
    if (!page) return;
    if (page.id !== 'LANDING') return;
    const isDynamedTab = tabValue === DYNAMED_TAB_VALUE;
    const option = isDynamedTab ? undefined : page.options?.[parseInt(tabValue, 10)];
    dispatch(
      tabPageSelected({
        documentVersionId: page.document_version_id,
        tabPageId: page.id,
        link: isDynamedTab ? DYNAMED_TAB_LABEL : option?.link || option?.text,
        tabValue,
      })
    );
    /*  Before making changes to this, inform:
        Cynthia.Walsh@providence.org
        Prateek.Fulsundar@providence.org

        If you modify this, please update the documentation at:
        https://pearl-project.atlassian.net/wiki/spaces/PearlArchitecture/pages/158662658/Mixpanel+and+Pendo

        The guide_tabs event is called when the user switches between tabs
        in the circle view.

        documentName: The name of the document version
        documentId: The id of the document
        documentVersionId: The id of the document version
        slug: The unique slug of the document (more reliable than title since title can be changed)
        persona: The persona of the document version
        careCategory: The care category of the document version
        patientGroup: The patient group of the document version
        tabName: The name of the tab
        session_id: The session id of the user
        */
    analyticsTrack(
      'guide_tabs',
      {
        documentName: documentVersion?.title,
        documentId: documentVersion?.document.id,
        documentVersionId: documentVersion?.id,
        slug: documentVersion?.document.slug,
        persona: documentVersion?.audiences.personas.join(','),
        careCategory: documentVersion?.audiences.care_categories.join(','),
        patientGroup: documentVersion?.audiences.patient_groups.join(','),
        tabName: isDynamedTab ? DYNAMED_TAB_LABEL : option?.link || option?.text,
      },
      pearlUserInfo
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue]);

  return (
    <TabContext value={tabValue}>
      <Box
        sx={{ borderBottom: 1, borderColor: 'divider' }}
        className={styles.tabs}
        data-is-reviewer={pearlUserInfo ? 'false' : 'true'}
      >
        <TabList onChange={(e, v) => setTabValue(v)} aria-label="Info" variant="scrollable" scrollButtons="auto">
          {page.options?.map((option, index) => (
            <Tab key={index} label={fixupGuideName(option.text)} value={index.toString()} />
          ))}
          {showDynamedTab && <DynamedTab value={DYNAMED_TAB_VALUE} />}
        </TabList>
        {toolbar}
      </Box>
      {children}
    </TabContext>
  );
}
